import {
    createApi,
    fakeBaseQuery,
} from '@reduxjs/toolkit/query';
import {
    GladosServiceFactory,
    TariffSimOnlyParamsV1,
    TariffWithHardwareParamsV1,
    UpsellParamsV1,
    HardwareDetailAtomicParamsV1,
    HardwareDetailGroupParamsV1,
    GladosEndpointVersion,
    AbstractedTariffSimOnlyResponse,
    AbstractedTariffWithHardwareResponse,
    AbstractedHardwareDetailGroupResponse,
    AbstractedHardwareDetailAtomicResponse,
    AbstractedUpsellResponse,
} from '@vfde-sails/glados-v2';
import { getQueryParam } from '@vfde-sails/utils';
import {
    BTX_BNT,
    MAP_BTX_TO_API_BTX,
    MAP_SALESCHANNEL_TO_API_SALESCHANNEL,
} from '@vfde-sails/constants';
import {
    selectAtomicId,
    selectSalesChannel,
    selectAtomicTariffId,
    selectSubscriptionId,
    selectDiscounts,
    selectDeviceId,
} from '../slice';
import { RootState } from '../../../store';
import { RequestError } from '@vfde-sails/types';
import { isAxiosError } from 'axios';

const createRequestError = (error: unknown): RequestError => {
    if (isAxiosError(error)) {
        return {
            code: error.code,
            status: error.response?.status,
            message: error.message,
        };
    }

    return {
        message: error instanceof Error ? error.message : `${error}`,
    };
};

const gladosApi = createApi({
    reducerPath: 'GladosService',
    baseQuery: fakeBaseQuery<RequestError>(),
    endpoints: build => ({
        getProducts: build.query<AbstractedUpsellResponse, void>({
            queryFn: async (_arg, api) => {
                const upsellService = GladosServiceFactory.getUpsellService();



                const state = api.getState() as RootState;

                const salesChannel = selectSalesChannel(state);
                const atomicId = selectAtomicId(state);
                const subscriptionId = selectAtomicTariffId(state);

                const params: UpsellParamsV1 = {
                    salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[salesChannel!]],
                    btx: MAP_BTX_TO_API_BTX[BTX_BNT],
                    ...atomicId && { atomicId },
                    ...subscriptionId && { tariffId: subscriptionId },
                } as UpsellParamsV1;

                try {
                    const response = await upsellService.getUpsellItems(GladosEndpointVersion.V1, params);

                    return { data: response.data };
                }
                catch (error) {
                    return {
                        error: createRequestError(error),
                    };
                }
            },
        }),
        getSimOnly: build.query<AbstractedTariffSimOnlyResponse, void>({
            queryFn: async (_arg, api) => {
                const tariffService = GladosServiceFactory.getTariffService();



                const state = api.getState() as RootState;
                const salesChannel = selectSalesChannel(state)!;
                const subscriptionId = selectSubscriptionId(state);
                const discounts = selectDiscounts(state);

                const params: TariffSimOnlyParamsV1 = {
                    salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[salesChannel]],
                    btx: MAP_BTX_TO_API_BTX[BTX_BNT],
                    tariffIds: [subscriptionId!],
                    discountIds: discounts,
                };

                try {
                    const response = await tariffService.getSimOnly(GladosEndpointVersion.V1, params);

                    return { data: response.data };
                }
                catch (error) {
                    return {
                        error: createRequestError(error),
                    };
                }
            },
        }),
        getTariffWithHardware: build.query<AbstractedTariffWithHardwareResponse, void>({
            queryFn: async (_arg, api) => {
                const tariffService = GladosServiceFactory.getTariffService();



                const state = api.getState() as RootState;
                const salesChannel = selectSalesChannel(state)!;
                const subscriptionId = selectSubscriptionId(state);
                const discounts = selectDiscounts(state);
                const atomicId = selectAtomicId(state);

                const params: TariffWithHardwareParamsV1 = {
                    salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[salesChannel]],
                    btx: MAP_BTX_TO_API_BTX[BTX_BNT],
                    discountIds: discounts,
                    tariffIds: [subscriptionId!],
                    atomicIds: [atomicId!],
                };

                try {
                    const response = await tariffService.getTariffWithHardware(GladosEndpointVersion.V1, params);

                    return { data: response.data };
                }
                catch (error) {
                    return {
                        error: createRequestError(error),
                    };
                }
            },
        }),
        getDeviceDetailGroup: build.query<AbstractedHardwareDetailGroupResponse, void>({
            queryFn: async (_arg, api) => {
                const hardwareService = GladosServiceFactory.getHardwareService();



                const state = api.getState() as RootState;
                const salesChannel = selectSalesChannel(state)!;
                const deviceId = selectDeviceId(state)!;

                const params: HardwareDetailGroupParamsV1 = {
                    salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[salesChannel!]],
                    btx: MAP_BTX_TO_API_BTX[BTX_BNT],
                    groupId: deviceId!,
                };

                try {
                    const response = await hardwareService.getHardwareDetailGroup(GladosEndpointVersion.V1, params);

                    return { data: response.data };
                }
                catch (error) {
                    return {
                        error: createRequestError(error),
                    };
                }
            },
        }),
        getDeviceDetailAtomic: build.query<AbstractedHardwareDetailAtomicResponse, void>({
            queryFn: async (_arg, api) => {
                const hardwareService = GladosServiceFactory.getHardwareService();



                const state = api.getState() as RootState;
                const salesChannel = selectSalesChannel(state)!;
                const atomicId = selectAtomicId(state)!;

                const params: HardwareDetailAtomicParamsV1 = {
                    atomicId,
                    salesChannel: [MAP_SALESCHANNEL_TO_API_SALESCHANNEL[salesChannel]],
                    btx: MAP_BTX_TO_API_BTX[BTX_BNT],
                };

                try {
                    const response = await hardwareService.getHardwareDetailAtomic(GladosEndpointVersion.V1, params);

                    return { data: response.data };
                }
                catch (error) {
                    return {
                        error: createRequestError(error),
                    };
                }
            },
        }),
    }),
});

export default gladosApi;

export const {
    getProducts,
    getSimOnly,
    getTariffWithHardware,
    getDeviceDetailGroup,
    getDeviceDetailAtomic,
} = gladosApi.endpoints;
